
import React, { useState, useEffect } from 'react';
import { useApplication } from '@/hooks/use-application';
import { ApplicationHeader } from '@/components/application/layout/ApplicationHeader';
import { ApplicationContent } from '@/components/application/layout/ApplicationContent';
import { ApplicationLoadingState } from '@/components/application/layout/ApplicationLoadingState';
import { ApplicationErrorState } from '@/components/application/layout/ApplicationErrorState';
import { ApplicationUnloadHandler } from '@/components/application/actions/ApplicationUnloadHandler';
import { useToast } from '@/hooks/use-toast';

const ApplicationPage = () => {
  // State management
  const [currentSection, setCurrentSection] = useState(0);
  const { toast } = useToast();
  const { 
    application, 
    setApplication,
    isLoading, 
    error, 
    lastSaved, 
    saveProgress,
    calculateProgress,
    debouncedSave 
  } = useApplication();

  // Debug what state we're in
  console.log('Application render state:', { 
    isLoading, 
    hasError: !!error, 
    hasApplication: !!application,
    currentSection,
    applicationId: application?.id,
    userId: application?.user_id
  });

  // Enhanced save handler with feedback
  const handleSave = async (data?: Partial<typeof application>) => {
    if (!application) return;
    try {
      console.log('Saving application data:', data || 'current state');
      await saveProgress(data);
      // Toast for successful save is shown inside saveProgress now
    } catch (err) {
      console.error('Save error:', err);
      toast({ 
        title: "Save failed", 
        description: "There was an error saving your progress. Please try again.",
        variant: "destructive"
      });
    }
  };

  // CRITICAL FIX: Add fallback render if application exists but is loading sections
  useEffect(() => {
    if (application) {
      console.log('Application loaded, current section:', currentSection);
    }
  }, [application, currentSection]);

  // Simple placeholder functions for header actions with improved save
  const handleSaveAndExit = async () => {
    if (application) {
      try {
        // Fix: Call saveProgress with no arguments instead of empty function
        await saveProgress();
        toast({ 
          title: "Progress saved", 
          description: "Your application has been saved. You can continue later." 
        });
        window.location.href = '/dashboard';
      } catch (err) {
        console.error('Save and exit error:', err);
        toast({ 
          title: "Save failed", 
          description: "There was an error saving your progress. Please try again.",
          variant: "destructive"
        });
      }
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handleLogout = async () => {
    if (application) {
      try {
        // Ensure we're calling saveProgress with an empty object to avoid type issues
        await saveProgress({});
        toast({ 
          title: "Progress saved", 
          description: "Your application has been saved. You can continue after logging back in."
        });
        window.location.href = '/login';
      } catch (err) {
        console.error('Save before logout error:', err);
        toast({ 
          title: "Save failed", 
          description: "There was an error saving your progress. Please try again.",
          variant: "destructive"
        });
      }
    } else {
      window.location.href = '/login';
    }
  };

  const handleReset = async () => {
    if (confirm('Are you sure you want to reset your application? This cannot be undone.')) {
      window.location.reload();
    }
  };

  // Create a properly typed saveProgress wrapper for the unload handler
  const handleUnloadSave = async (): Promise<void> => {
    if (application) {
      try {
        // Call saveProgress but ignore the boolean return
        await saveProgress({});
        // Void return type
      } catch (error) {
        console.error('Error saving on unload:', error);
        // Still void return type even with error
      }
    }
  };

  // Add unload handler to save progress when navigating away
  if (application) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex flex-col relative overflow-hidden">
        {/* Add unload handler */}
        <ApplicationUnloadHandler 
          application={application} 
          saveProgress={handleUnloadSave} // Fix: Pass a function that returns Promise<void>
        />
        
        {/* Background elements */}
        <div className="absolute -top-20 right-0 w-96 h-96 rounded-full bg-primary/5 -z-10 blur-3xl"></div>
        <div className="absolute -bottom-40 left-0 w-64 h-64 rounded-full bg-blue-500/5 -z-10 blur-3xl"></div>
        
        <ApplicationHeader 
          onSaveAndExit={handleSaveAndExit}
          onLogout={handleLogout}
          onReset={handleReset}
          isLoading={isLoading}
        />
        <div className="flex-1">
          <ApplicationContent
            application={application}
            currentSection={currentSection}
            setCurrentSection={setCurrentSection}
            lastSaved={lastSaved}
            progress={calculateProgress()}
            onSave={handleSave}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  // CRITICAL: Add a safety mechanism to render even if sections are loading
  if (isLoading) {
    console.log("Rendering loading state");
    return <ApplicationLoadingState />;
  }

  if (error) {
    console.log("Rendering error state:", error);
    return <ApplicationErrorState />;
  }

  console.log("No application available, rendering error state");
  return <ApplicationErrorState />;
};

export default ApplicationPage;
